import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface ProgressCellProps {
  value: number | null;
  onChange: (value: number | null) => void;
  readOnly?: boolean;
}

export function ProgressCell({ value, onChange, readOnly }: ProgressCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value?.toString() || '0');

  const progress = value || 0;

  const getProgressColor = (percent: number) => {
    if (percent < 34) return 'bg-red-500';
    if (percent < 67) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const handleSave = () => {
    const num = parseInt(tempValue);
    if (isNaN(num)) {
      onChange(0);
    } else {
      const clamped = Math.max(0, Math.min(100, num));
      onChange(clamped);
    }
    setIsEditing(false);
  };

  if (readOnly) {
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full transition-all ${getProgressColor(progress)}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-700 min-w-[45px] text-right">
          {progress}%
        </span>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[200px]">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="0"
            max="100"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            className="w-20"
            autoFocus
          />
          <span className="text-sm">%</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave}>
            Salvar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 w-full cursor-pointer hover:bg-gray-50 p-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full transition-all ${getProgressColor(progress)}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="text-sm font-medium text-gray-700 min-w-[45px] text-right">
        {progress}%
      </span>
    </div>
  );
}
